import { useMemo, useState } from 'react';
import {
  NOTIFICATION_CATALOG,
  type NotificationAudienceKind,
  type NotificationCatalogEntry,
} from '@peles/shared';
import { EntityList, StatusBadge, type EntityListColumn } from '../common/EntityList';

const PAGE_SIZE = 10;

const AUDIENCE_LABELS: Record<NotificationAudienceKind, string> = {
  office: 'Office',
  person: 'Person concerned',
  client: 'Clients',
  vendor: 'Vendors',
  guest: 'Guests / public',
};

const CHANNEL_LABELS = { in_app: 'In app', email: 'Email' } as const;

/** Who besides the office an event's group may send it to. */
const CLIENT_MEMBERS_LABEL = {
  always: "An event's group may include its clients",
  fullBudget: "An event's group may include its clients, while they see the budget in full",
} as const;

function matches(entry: NotificationCatalogEntry, term: string): boolean {
  if (!term) return true;
  return `${entry.label} ${entry.trigger} ${entry.category} ${entry.defaultAudience} ${entry.key}`
    .toLowerCase()
    .includes(term);
}

const columns: EntityListColumn<NotificationCatalogEntry>[] = [
  {
    key: 'label',
    header: 'Notification',
    sortable: true,
    sortValue: (e) => e.label,
    render: (e) => (
      <div>
        <div className="font-medium text-gray-900">{e.label}</div>
        <div className="text-xs text-gray-500">{e.trigger}</div>
      </div>
    ),
  },
  {
    key: 'level',
    header: 'Level',
    sortable: true,
    sortValue: (e) => e.level,
    render: (e) => (
      <StatusBadge
        label={e.level === 'global' ? 'Global' : 'Event'}
        color={e.level === 'global' ? 'purple' : 'blue'}
      />
    ),
  },
  {
    key: 'audience',
    header: 'Who receives it',
    hideOnMobile: true,
    render: (e) => (
      <div>
        <div className="text-sm text-gray-700">{e.defaultAudience}</div>
        <div className="text-xs text-gray-500">
          {AUDIENCE_LABELS[e.audience]} · {e.channels.map((c) => CHANNEL_LABELS[c]).join(' + ')}
        </div>
      </div>
    ),
  },
  {
    key: 'routable',
    header: 'Group',
    sortable: true,
    sortValue: (e) => (e.routable ? 0 : 1),
    render: (e) =>
      e.routable ? (
        <div className="space-y-1">
          <StatusBadge label="Can route to a group" color="green" />
          {e.clientMembers && (
            <div className="text-xs text-gray-500">{CLIENT_MEMBERS_LABEL[e.clientMembers]}</div>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-500">Fixed</span>
      ),
  },
];

/**
 * Every email and notification the app sends — what sets it off, global or
 * per event, who it reaches and whether a distribution group may take it
 * over. The catalog is `NOTIFICATION_CATALOG` (`@peles/shared`), keyed by the
 * same template keys the wording is edited under.
 */
export function NotificationCatalogList() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const visible = useMemo(
    () => NOTIFICATION_CATALOG.filter((e) => matches(e, search.trim().toLowerCase())),
    [search],
  );
  const pageItems = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <EntityList
      title="Every notification the app sends"
      items={pageItems}
      columns={columns}
      getKey={(e) => e.key}
      searchValue={search}
      onSearchChange={(value) => {
        setSearch(value);
        setPage(1);
      }}
      searchPlaceholder="Search notifications…"
      page={page}
      pageSize={PAGE_SIZE}
      total={visible.length}
      onPageChange={setPage}
      emptyMessage="No notification matches that."
      renderCard={(e) => (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-gray-900">{e.label}</span>
            <StatusBadge
              label={e.level === 'global' ? 'Global' : 'Event'}
              color={e.level === 'global' ? 'purple' : 'blue'}
            />
          </div>
          <div className="text-xs text-gray-500">{e.trigger}</div>
          <div className="text-sm text-gray-700">{e.defaultAudience}</div>
          {e.routable && <StatusBadge label="Can route to a group" color="green" />}
        </div>
      )}
    />
  );
}
